/* Lightweight collection quick add. Product data loads on demand; no product-page scripts are injected. */
(() => {
  'use strict';
  const dialog = document.getElementById('em-quick-add');
  if (!dialog || !dialog.showModal) return;
  const find = (selector) => dialog.querySelector(selector);
  const content = find('.em-qa-content');
  const loading = find('.em-qa-loading');
  const options = find('.em-qa-options');
  const submit = find('.em-qa-submit');
  const status = find('.em-qa-status');
  const quantity = find('[name="quantity"]');
  const productLink = find('.em-qa-product');
  const cartLink = find('.em-qa-cart');
  const root = dialog.dataset.root.replace(/\/?$/, '/');
  const money = new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
    style: 'currency', currencyDisplay: 'narrowSymbol', currency: dialog.dataset.currency || 'CAD'
  });
  let product, selected, variant, opener, request, pending = false, photoIndex = 0;
  let productURL;
  let swatches = [];
  const formatPrice = (cents) => money.format(cents / 100) + ' ' + (dialog.dataset.currency || 'CAD');
  const photos = () => product.images || [];
  const photoURL = (photo) => typeof photo === 'string' ? photo : photo.src;
  function close(restoreFocus = true) {
    if (!dialog.open) return;
    request?.abort();
    dialog.close();
    document.documentElement.classList.remove('em-qa-open');
    if (restoreFocus && opener?.isConnected) opener.focus({ preventScroll: true });
  }
  function message(text, error = false) {
    status.textContent = text;
    status.toggleAttribute('data-error', error);
  }
  function showPhoto(index) {
    photoIndex = index;
    const source = photos()[index];
    const image = find('.em-qa-image');
    image.hidden = !source;
    if (source) {
      const url = new URL(photoURL(source), location.origin);
      url.searchParams.set('width', '800');
      image.src = url.href;
      image.alt = `${product.title} — photo ${index + 1}`;
    }
    find('.em-qa-thumbs').querySelectorAll('button').forEach((button, i) => {
      button.setAttribute('aria-pressed', String(i === index));
    });
  }
  function renderPhotos() {
    const thumbs = find('.em-qa-thumbs');
    thumbs.replaceChildren();
    photos().forEach((photo, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', `View photo ${index + 1}`);
      const image = document.createElement('img');
      const url = new URL(photoURL(photo), location.origin);
      url.searchParams.set('width', '100');
      image.src = url.href; image.alt = ''; image.loading = 'lazy';
      button.append(image);
      button.addEventListener('click', () => showPhoto(index));
      thumbs.append(button);
    });
    thumbs.hidden = photos().length < 2;
    showPhoto(0);
  }
  function update() {
    variant = selected.every(value => value !== null) ? product.variants.find(item =>
      item.options.every((value, index) => value === selected[index])) : null;
    const candidates = product.variants.filter(item => selected.every((value, i) => value === null || value === item.options[i]));
    const displayVariant = variant || candidates.find(item => item.available) || candidates[0];
    const price = find('.em-qa-price');
    price.replaceChildren();
    if (displayVariant) {
      price.append(document.createTextNode(formatPrice(displayVariant.price)));
      if (displayVariant.compare_at_price > displayVariant.price) {
        const compare = document.createElement('s');
        compare.textContent = formatPrice(displayVariant.compare_at_price); price.append(compare);
      }
      if (displayVariant.featured_image?.src) {
        const fileName = (source) => new URL(source, location.origin).pathname.split('/').pop();
        const imageIndex = photos().findIndex(photo => fileName(photoURL(photo)) === fileName(displayVariant.featured_image.src));
        if (imageIndex >= 0 && imageIndex !== photoIndex) showPhoto(imageIndex);
        else if (imageIndex < 0) {
          const source = new URL(displayVariant.featured_image.src, location.origin);
          source.searchParams.set('width', '800');
          find('.em-qa-image').src = source.href;
        }
      }
    }
    options.querySelectorAll('fieldset').forEach((fieldset, index) => {
      const current = selected[index];
      fieldset.querySelector('legend').textContent = `Select ${product.options[index].name}` + (current === null ? '' : `: ${current}`);
      fieldset.querySelectorAll('button').forEach(button => {
        const value = button.dataset.value;
        // Downstream options follow upstream selections; colours remain switchable even if size is unavailable.
        const available = product.variants.some(item => item.available && item.options[index] === value &&
          selected.every((choice, i) => i >= index || choice === null || choice === item.options[i]));
        button.disabled = !available || pending;
        button.setAttribute('aria-pressed', String(value === current));
        button.setAttribute('aria-label', available ? value : `${value} — sold out`);
      });
    });
    const needsChoice = selected.findIndex(value => value === null);
    const restricted = product.requires_selling_plan || /gift\s*card/i.test(product.type || '');
    submit.disabled = pending || !variant?.available || restricted;
    submit.textContent = pending ? 'Adding…' : restricted ? 'Choose options on product page' : !product.available ? 'Sold out' :
      needsChoice >= 0 ? `Select ${product.options[needsChoice].name.toLowerCase()}` : !variant?.available ? 'Sold out' : 'Add to Cart';
    quantity.disabled = pending;
    const rule = variant?.quantity_rule;
    quantity.min = String(rule?.min || 1); quantity.step = String(rule?.increment || 1);
    if (rule?.max) quantity.max = String(rule.max); else quantity.removeAttribute('max');
    if (+quantity.value < +quantity.min) quantity.value = quantity.min;
    updateQuantityButtons();
    const url = new URL(productURL);
    if (variant) url.searchParams.set('variant', variant.id); else url.searchParams.delete('variant');
    productLink.href = url.href;
  }
  function renderOptions(initial) {
    selected = product.options.map((option, index) => {
      if (product.variants.length === 1 && option.name === 'Title') return initial.options[index];
      // Require explicit size selection to prevent an accidental first-size purchase.
      return /size/i.test(option.name) ? null : initial.options[index];
    });
    options.replaceChildren();
    product.options.forEach((option, index) => {
      if (product.variants.length === 1 && option.name === 'Title' && option.values[0] === 'Default Title') return;
      const fieldset = document.createElement('fieldset');
      fieldset.append(document.createElement('legend'));
      const choices = document.createElement('div'); choices.className = 'em-qa-choices';
      option.values.forEach(value => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'em-qa-choice'; button.textContent = value; button.dataset.value = value;
        if (/size/i.test(option.name)) button.classList.add('em-qa-size');
        const swatch = swatches.find(item => item.option === option.name && item.value === value);
        if (swatch) {
          button.classList.add('em-qa-colour');
          button.title = value;
          const chip = document.createElement('span'); chip.className = 'em-qa-colour-chip'; chip.setAttribute('aria-hidden', 'true');
          if (swatch.image) chip.style.backgroundImage = `url("${swatch.image}")`;
          else chip.style.backgroundColor = swatch.colour;
          button.replaceChildren(chip);
        }
        button.addEventListener('click', () => {
          selected[index] = value;
          // Reset dependent selections only when that combination does not exist or is sold out.
          if (!product.variants.some(item => item.available && selected.every((choice, i) => choice === null || choice === item.options[i]))) {
            selected = selected.map((choice, i) => i > index && product.options[i].values.length > 1 ? null : choice);
          }
          message(''); cartLink.hidden = true; update();
        });
        choices.append(button);
      });
      fieldset.append(choices); options.append(fieldset);
    });
  }
  async function open(trigger) {
    if (pending) return;
    opener = trigger;
    swatches = [...(trigger.parentElement.querySelector('.em-qa-swatch-data')?.content.querySelectorAll('[data-value]') || [])].map(node => ({ ...node.dataset }));
    request?.abort(); request = new AbortController();
    const controller = request;
    const url = new URL(trigger.href);
    productURL = url.origin + url.pathname;
    productLink.href = trigger.href;
    content.hidden = true; loading.hidden = false; loading.textContent = 'Loading product…';
    find('#em-qa-title').textContent = 'Quick add'; message(''); cartLink.hidden = true;
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('em-qa-open'); dialog.scrollTop = 0;
    try {
      const response = await fetch(`${url.pathname}.js`, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Product unavailable');
      const data = await response.json();
      if (controller !== request || !dialog.open) return;
      product = data;
      if (!product.variants?.length) throw new Error('Product unavailable');
      const initial = product.variants.find(item => String(item.id) === url.searchParams.get('variant')) ||
        product.variants.find(item => item.available) || product.variants[0];
      find('#em-qa-title').textContent = product.title;
      const description = new DOMParser().parseFromString(product.description || '', 'text/html').body.textContent.trim().replace(/\s+/g, ' ');
      find('.em-qa-description').textContent = description.length > 200 ? description.slice(0, 197) + '…' : description;
      quantity.value = '1'; renderPhotos(); renderOptions(initial); update();
      content.hidden = false; loading.hidden = true;
    } catch (error) {
      if (error.name === 'AbortError' || controller !== request || !dialog.open) return;
      loading.textContent = 'This product couldn’t be loaded. ';
      const link = document.createElement('a'); link.href = trigger.href; link.textContent = 'View product details'; loading.append(link);
    }
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-em-quick-add]');
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault(); open(trigger);
  });
  find('.em-qa-close').addEventListener('click', () => close());
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
  });
  dialog.addEventListener('close', () => document.documentElement.classList.remove('em-qa-open'));
  // A native modal is above all page layers; release it before the existing Smart Cart opens.
  document.addEventListener('rebuy:smartcart.show', () => close(false));
  function updateQuantityButtons() {
    const down = find('[data-em-qa-quantity="down"]');
    const up = find('[data-em-qa-quantity="up"]');
    down.disabled = pending || +quantity.value <= +quantity.min;
    up.disabled = pending || (quantity.max !== '' && +quantity.value >= +quantity.max);
  }
  dialog.querySelectorAll('[data-em-qa-quantity]').forEach(button => button.addEventListener('click', () => {
    if (pending) return;
    if (button.dataset.emQaQuantity === 'up') quantity.stepUp(); else quantity.stepDown();
    updateQuantityButtons();
  }));
  quantity.addEventListener('input', updateQuantityButtons);
  find('form').addEventListener('submit', async event => {
    event.preventDefault();
    if (pending || !variant?.available || !quantity.reportValidity()) return;
    pending = true; message(''); update();
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: Number(quantity.value) }] })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'This item couldn’t be added.');
      message('Added to your cart.'); cartLink.hidden = false;
      // The add succeeded. Cart refresh failure must never be reported as a failed add or retried.
      try {
        const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          document.querySelectorAll('#site-control .cart:not(.nav-search)').forEach(link => {
            const count = link.querySelector('div');
            const label = link.querySelector('.text-link');
            if (count) count.textContent = cart.item_count;
            if (label) label.textContent = `Cart (${cart.item_count})`;
          });
        }
        const rebuy = window.Rebuy;
        if (rebuy?.SmartCart && document.body.classList.contains('smart-cart--enabled')) {
          close(false);
          if (typeof rebuy.Cart?.fetchCart === 'function') rebuy.Cart.fetchCart();
          if (typeof rebuy.SmartCart.show === 'function' && rebuy.SmartCart.skip_open !== true) rebuy.SmartCart.show();
        }
      } catch (error) { /* Inline success and the View cart link remain usable. */ }
    } catch (error) {
      message(error.message === 'Failed to fetch' ? 'We couldn’t confirm the cart update. Please check your cart before trying again.' : error.message, true);
      cartLink.hidden = false;
    } finally { pending = false; update(); }
  });
})();
